<script setup lang="ts">
import type {
  SoftwareDeviceType,
  SoftwareHomeDeviceData,
  SoftwareItem,
  SoftwarePlatformData,
  SoftwareRankingData,
} from '~/api/software'
import { isChineseLocale } from '~/composables/useAppI18n'
import { formatSoftwareNumber, selectSoftwareRankingList } from '~/utils/software'
import areaCodeCn from '~/assets/json/areaCodeCN.json'
import areaCodeEn from '~/assets/json/areaCodeEn.json'
import downloadIcon from '~/assets/icon/download.svg?url'
import globalIcon from '~/assets/icon/global.svg?url'
import ArrowRight from '~/assets/icon/arrow-right.svg'
/** 月度活跃排行数值图标。 */
const ACTIVE_RANKING_ICON =
  'https://resources1.interface003.com/web30/2026/09/16/359c78f3-5c52-4cd5-90d8-47ec6c22ab53.png'

type RankingType = 'active' | 'download'
type RankingScope = 'global' | 'current'
type CountryItem = {
  countryCode?: string
  name?: string
  flag?: string
  'flag-c'?: string
}

const SOFTWARE_RANK_BADGE_IMAGES: Record<number, string> = {
  1: 'https://resources1.interface003.com/web30/2026/09/10/b6fd2280-5a67-4cb1-acc6-887f4b35a178.png',
  2: 'https://resources1.interface003.com/web30/2026/09/10/73df3796-fbd1-4e9a-a75f-6aa048b695ac.png',
  3: 'https://resources1.interface003.com/web30/2026/09/10/8494a52a-7c00-4bc7-8c54-420b02eb4cf3.png',
}

const { locale, localePath, t } = useAppI18n()
const { isRtl } = useRtl()
const appStore = useAppStore()
const props = defineProps<{
  softwareHomeData?: SoftwarePlatformData<SoftwareHomeDeviceData>
  deviceType?: SoftwareDeviceType
}>()
const deviceType = computed(() => props.deviceType ?? 1)
const rankingType = ref<RankingType>('active')
const rankingScope = ref<RankingScope>('global')
const isRankingDialogVisible = ref(false)
const countryGroups = computed(() => (isChineseLocale(locale) ? areaCodeCn : areaCodeEn))
const countries = computed(() => countryGroups.value.flatMap((group) => group.data as CountryItem[]))
const currentCountry = computed(() => {
  return countries.value.find((country) => String(country.countryCode) === String(appStore.CountryCode))
})
const currentCountryName = computed(
  () => currentCountry.value?.name || (isChineseLocale(locale) ? '英国' : 'United Kingdom'),
)
const currentCountryFlag = computed(
  () =>
    currentCountry.value?.['flag-c'] ||
    currentCountry.value?.flag ||
    'https://resources1.interface003.com/web30/2026/08/03/d47e1f9a-325f-43ac-af7d-027339196525.svg',
)
const platformKey = computed(() => (deviceType.value === 1 ? 'ios' : 'android'))
const rankingData = computed(() => {
  const platformData = rankingType.value === 'active' ? 'active' : 'download'
  return props.softwareHomeData?.[platformKey.value]?.[platformData]
})
const availableRankingTypes = computed(() =>
  (['active', 'download'] as const).filter((type) => {
    const data = props.softwareHomeData?.[platformKey.value]?.[type]
    return Boolean(data?.global.length || data?.current.length)
  }),
)
/** 当前排行类型下有数据的榜单范围（全球 / 本地）。 */
const availableRankingScopes = computed<RankingScope[]>(() =>
  (['global', 'current'] as const).filter((scope) => Boolean(rankingData.value?.[scope]?.length)),
)
const hasRankingData = computed(() => availableRankingTypes.value.length > 0)
const hasMoreRanking = computed(() => selectSoftwareRankingList(rankingData.value, rankingScope.value).length > 6)
const rankingList = computed(() => selectSoftwareRankingList(rankingData.value, rankingScope.value).slice(0, 6))
const activeRankingData = computed<SoftwarePlatformData<SoftwareRankingData>>(() => ({
  ios: props.softwareHomeData?.ios?.active ?? { global: [], current: [] },
  android: props.softwareHomeData?.android?.active ?? { global: [], current: [] },
}))
const downloadRankingData = computed<SoftwarePlatformData<SoftwareRankingData>>(() => ({
  ios: props.softwareHomeData?.ios?.download ?? { global: [], current: [] },
  android: props.softwareHomeData?.android?.download ?? { global: [], current: [] },
}))

watch(
  availableRankingTypes,
  (types) => {
    if (types.length && !types.includes(rankingType.value)) rankingType.value = types[0]
  },
  { immediate: true },
)

watch(
  availableRankingScopes,
  (scopes) => {
    if (scopes.length && !scopes.includes(rankingScope.value)) rankingScope.value = scopes[0]
  },
  { immediate: true },
)

/** 从页签入口读取排行类型，避免模板内联修改状态。 */
function handleSelectRankingType(event: Event) {
  const nextType = (event.currentTarget as HTMLElement).dataset.rankingType
  if (nextType === 'active' || nextType === 'download') rankingType.value = nextType
}

/** 从页签入口读取榜单范围，当前国家与全球各自展示独立数据。 */
function handleSelectRankingScope(event: Event) {
  const nextScope = (event.currentTarget as HTMLElement).dataset.rankingScope
  if (nextScope === 'global' || nextScope === 'current') rankingScope.value = nextScope
}

/** 打开当前排行类型和榜单范围对应的完整榜单弹窗。 */
function handleOpenRankingDialog() {
  if (hasMoreRanking.value) isRankingDialogVisible.value = true
}

/** 关闭完整榜单弹窗。 */
function handleCloseRankingDialog() {
  isRankingDialogVisible.value = false
}

/** 生成首页软件详情页路径，允许详情字段为空。 */
function buildHomeSoftwareDetailsPath(item: SoftwareItem) {
  const marketId = String(item.marketId ?? '').trim()
  const appBundleId = String(item.appBundleId ?? '').trim()
  const traderCode = String(item.traderCode ?? '').trim()
  const platformSuffix = marketId === '11' ? 2 : 1
  return `/crypto-apps/${marketId}-${appBundleId}-${traderCode}-${platformSuffix}?fromType=1`
}

/** 为 div 交互入口补齐 Enter 与 Space 键盘行为。 */
function handleKeyboardActivate(event: KeyboardEvent) {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  ;(event.currentTarget as HTMLElement).click()
}

const scopeTabRefs = ref<Array<HTMLElement | null>>([])

/** 保存每个范围 Tab 按钮的 DOM 引用，支持键盘焦点循环。 */
function setScopeTabRef(el: Element | any, index: number) {
  if (el instanceof HTMLElement) {
    scopeTabRefs.value[index] = el
    return
  }
  scopeTabRefs.value[index] = null
}

/** 键盘焦点到达最后一个范围后，按 Tab 从首个范围继续循环。 */
function onScopeTabKeydown(event: KeyboardEvent, index: number) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    ;(event.currentTarget as HTMLElement).click()
    return
  }

  const total = availableRankingScopes.value.length
  if (total < 2) return

  if (event.key === 'Tab') {
    if (event.shiftKey && index === 0) {
      event.preventDefault()
      scopeTabRefs.value[total - 1]?.focus()
    } else if (!event.shiftKey && index === total - 1) {
      event.preventDefault()
      scopeTabRefs.value[0]?.focus()
    }
  }
}

/** 取接口排名，接口未返回时按当前展示顺序补齐。 */
function getRankNumber(rankNo: number | undefined, index: number) {
  return rankNo || index + 1
}

/** 根据排名返回图片角标地址，非前三名返回空字符串。 */
function getSoftwareRankBadgeImage(rankNo: number) {
  return SOFTWARE_RANK_BADGE_IMAGES[rankNo] ?? ''
}

/** 前三名使用图片角标，其余排名使用浅灰底。 */
function getRankClass(rankNo: number) {
  if (rankNo <= 3) return ''
  return 'bg-[#f2f3f5] text-[#7d818a]'
}
</script>

<template>
  <section v-if="hasRankingData" id="monthly-ranking" class="w-full text-[#292a2e]">
    <span id="rank" class="block h-0 scroll-mt-[70px]" aria-hidden="true"></span>
    <div class="flex h-[60px] items-center justify-between">
      <div class="-mx-[12px] flex h-full items-start border-b-[0.5px] border-b-[rgba(5,21,36,0.06)]" role="tablist">
        <div
          v-for="type in availableRankingTypes"
          :key="type"
          role="tab"
          tabindex="0"
          :data-ranking-type="type"
          :aria-selected="rankingType === type"
          class="relative mx-[12px] flex h-full cursor-pointer items-start pt-[16px] text-[20px] leading-[28px] whitespace-nowrap"
          :class="
            rankingType === type ? 'font-semibold text-[#292a2e]' : 'font-normal text-[#8c8f97] hover:text-[#8c8f97]/80'
          "
          @click="handleSelectRankingType"
          @keydown="handleKeyboardActivate"
        >
          {{ t(type === 'active' ? '89331' : '89330') }}
          <span
            v-if="rankingType === type"
            class="absolute inset-x-[0px] bottom-[0px] mx-auto h-[4px] w-[28px] rounded-[999px] bg-[#292a2e]"
          />
        </div>
      </div>

      <div
        v-if="hasMoreRanking"
        role="button"
        tabindex="0"
        :aria-label="t('storeHomeViewAll')"
        class="flex size-[32px] cursor-pointer items-center justify-center rounded-full outline-none rtl:rotate-180 hover:[&_.store-arrow_path]:!fill-[#292A2E]"
        @click="handleOpenRankingDialog"
        @keydown="handleKeyboardActivate"
      >
        <ArrowRight class="store-arrow size-[20px]" aria-hidden="true" />
      </div>
    </div>

    <div v-if="availableRankingScopes.length" class="relative mt-[8px] h-[60px] overflow-hidden">
      <div class="-mx-[4px] flex h-full items-start overflow-x-auto overflow-y-hidden py-[12px] pl-[2px] rtl:pl-0 rtl:pr-[2px]" role="tablist">
        <button
          v-for="(scope, index) in availableRankingScopes"
          :key="scope"
          :ref="(el) => setScopeTabRef(el, index)"
          type="button"
          role="tab"
          tabindex="0"
          :data-ranking-scope="scope"
          :aria-selected="rankingScope === scope"
          class="ranking-scope-tab relative mx-[4px] box-border flex h-[36px] shrink-0 cursor-pointer items-center overflow-visible rounded-[2px] border-[0.5px] px-[12px] text-[13px] leading-[20px] whitespace-nowrap"
          :class="[
            { 'is-rtl': isRtl, 'is-active': rankingScope === scope },
            rankingScope === scope
              ? 'border-[#112D73] bg-[#112D73] font-[500] text-white'
              : 'border-[rgba(11,18,40,0.14)] bg-white font-normal text-[#292a2e] hover:bg-[#f2f3f5]/80 hover:text-[#1d2129]/80',
          ]"
          @click="handleSelectRankingScope"
          @keydown="onScopeTabKeydown($event, index)"
        >
          <CommonImage
            v-if="scope === 'global'"
            :src="globalIcon"
            alt=""
            :width="16"
            :height="16"
            loading="eager"
            :class="rankingScope === scope ? 'brightness-0 invert' : ''"
          />
          <CommonImage
            v-else
            :src="currentCountryFlag"
            :alt="currentCountryName"
            width="16"
            height="16"
            loading="eager"
            class="h-[16px] w-[16px] shrink-0 rounded-[999px] border-[0.5px] border-solid border-[rgba(5,21,36,0.12)] object-cover"
          />
          <span class="pl-[4px] rtl:pr-[4px] rtl:pl-0">
            {{ scope === 'global' ? t('89337') : t('89338', ['{0}']).replace('{0}', currentCountryName) }}
          </span>
        </button>
      </div>
      <div
        aria-hidden="true"
        class="pointer-events-none absolute top-[0px] right-[0px] h-[60px] w-[32px] bg-gradient-to-l from-white to-white/0 rtl:right-auto rtl:left-[0px] rtl:bg-gradient-to-r"
      />
    </div>

    <div v-if="rankingList.length" class="-mx-[6px] mt-[8px] flex h-[168px] items-center justify-start">
      <a
        v-for="(item, index) in rankingList"
        :key="`${item.appBundleId}-${index}`"
        :href="localePath(buildHomeSoftwareDetailsPath(item))"
        target="_blank"
        rel="noopener noreferrer"
        :class="[
          'relative mx-[6px] flex h-[168px] w-[190px] min-w-[190px] shrink-0 cursor-pointer flex-col items-center justify-center overflow-visible rounded-[12px] border-[0.5px] !border-[rgba(11,18,40,0.14)] border-transparent p-[24px] hover:bg-[#F8F8F8]',
          { 'is-rtl': isRtl },
        ]"
      >
        <CommonImage
          :src="item.logo"
          :alt="item.name"
          :width="52"
          :height="52"
          loading="eager"
          class="box-border shrink-0 rounded-[6px] border border-solid border-[rgba(5,21,36,0.06)]"
        />
        <div class="mt-[16px] flex max-w-full min-w-[0px] flex-col items-center justify-center">
          <div class="max-w-[159px] truncate text-center text-[18px] leading-[26px] font-semibold" :title="item.name">
            {{ item.name }}
          </div>
          <div class="-mx-[2px] mt-[4px] flex items-center justify-center text-[#7d818a]">
            <CommonImage
              :src="rankingType === 'active' ? ACTIVE_RANKING_ICON : downloadIcon"
              alt=""
              :width="14"
              :height="14"
              loading="eager"
              class="mx-[2px] opacity-60"
            />
            <span class="font-wiki-Medium mx-[2px] text-[14px] leading-[22px]">
              {{ formatSoftwareNumber(rankingType === 'download' ? item.downloadNum : item.activeValue) }}
            </span>
          </div>
        </div>
        <div
          class="absolute top-[-0.5px] left-[-0.5px] flex size-[26px] items-center justify-center rounded-tl-[12px] rounded-br-[12px] text-[14px] leading-[18px] font-[500] italic rtl:right-[-0.5px] rtl:left-auto rtl:rounded-tl-[0px] rtl:rounded-tr-[12px] rtl:rounded-br-[0px] rtl:rounded-bl-[12px]"
          :class="getRankClass(getRankNumber(item.rankNo, index))"
        >
          <CommonImage
            v-if="getSoftwareRankBadgeImage(getRankNumber(item.rankNo, index))"
            :src="getSoftwareRankBadgeImage(getRankNumber(item.rankNo, index))"
            alt=""
            :width="26"
            :height="26"
            loading="eager"
            object-fit="contain"
          />
          <span v-else>{{ getRankNumber(item.rankNo, index) }}</span>
        </div>
      </a>
    </div>
    <div v-else class="mt-[8px] flex h-[168px] items-center justify-center text-[13px] leading-[20px] text-[#8c8f97]">
      {{ t('storeHomeEmpty') }}
    </div>

    <HomeSoftwareRankingDialog
      :visible="isRankingDialogVisible"
      :ranking-type="rankingType"
      :ranking-scope="rankingScope"
      :initial-device-type="deviceType"
      :active-data="activeRankingData"
      :download-data="downloadRankingData"
      :country-name="currentCountryName"
      @close="handleCloseRankingDialog"
    />
  </section>
</template>

<style scoped lang="less">
.ranking-scope-tab {
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(17, 45, 115, 0.3);
  }
}

.is-active {
  &:hover:not([aria-disabled='true']) {
    background: linear-gradient(0deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), #112d73;
  }
}
</style>
